import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getClubPayments,
  refundClubPayment,
  downloadClubPaymentsCsv,
  formatMoney,
  type ClubPayment,
} from "../../services/payments";
import { AdminCard } from "../admin/AdminCard";
import DateInput from "../DateInput";

/**
 * The online money, after it has arrived: what each parent paid, what the
 * processor kept, what the club received — and the two things an admin
 * actually needs to do with it, give some of it back and hand the lot to an
 * accountant.
 *
 * Gross and net are both shown because they are different numbers and the club
 * owes its books both: the parent paid the gross, the bank received the net,
 * and the difference is a cost the club incurred by collecting by card.
 */

/** First day of three months ago, and today, as yyyy-mm-dd. */
function defaultRange() {
  const pad = (n: number) => String(n).padStart(2, "0");
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: fmt(start), to: fmt(now) };
}

const MIN_REASON = 4;

export default function ClubOnlinePayments({ clubId }: { clubId: string }) {
  const { t } = useTranslation();

  const [rows, setRows] = useState<ClubPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const range = useMemo(defaultRange, []);
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // The refund dialog. Kept as the row itself rather than an id, so the dialog
  // can show what is being refunded without looking it up again.
  const [refunding, setRefunding] = useState<ClubPayment | null>(null);
  const [reason, setReason] = useState("");
  const [partial, setPartial] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setRows(await getClubPayments(clubId));
    } catch (e) {
      console.error("Failed to load club payments:", e);
      setError(true);
    }
    setLoading(false);
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  const openRefund = (row: ClubPayment) => {
    setRefunding(row);
    setReason("");
    setPartial("");
    setRefundError(null);
  };

  const submitRefund = async () => {
    if (!refunding) return;
    const trimmed = reason.trim();
    if (trimmed.length < MIN_REASON) {
      setRefundError(t("onlinePayments.needReason", "Scrie un motiv pentru restituire."));
      return;
    }

    // An empty box means the whole remaining amount; anything typed is read as
    // RON and must fit inside what is left.
    let amountCents: number | null = null;
    if (partial.trim()) {
      const parsed = Math.round(Number(partial.replace(",", ".")) * 100);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setRefundError(t("onlinePayments.badAmount", "Sumă invalidă."));
        return;
      }
      if (parsed > refunding.refundable_cents) {
        setRefundError(
          t("onlinePayments.tooMuch", "Mai mult decât se poate restitui ({{max}}).", {
            max: formatMoney(refunding.refundable_cents, refunding.currency),
          })
        );
        return;
      }
      amountCents = parsed;
    }

    setSubmitting(true);
    setRefundError(null);
    try {
      const res = await refundClubPayment(clubId, {
        ledgerId: refunding.ledger_id,
        reason: trimmed,
        amountCents,
      });
      setRefunding(null);
      setDone(
        t("onlinePayments.refunded", "S-au restituit {{amount}}. Banii ajung la plătitor în câteva zile lucrătoare.", {
          amount: formatMoney(res.refunded_cents, refunding.currency),
        })
      );
      // The ledger row for the refund arrives by webhook, so the list is
      // reloaded rather than patched in place.
      await load();
    } catch (e: any) {
      setRefundError(
        e?.response?.data?.error || t("onlinePayments.refundFailed", "Restituirea nu a reușit.")
      );
    }
    setSubmitting(false);
  };

  const runExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadClubPaymentsCsv(clubId, from, to);
    } catch (e) {
      console.error("Export failed:", e);
      setExportError(t("onlinePayments.exportFailed", "Exportul nu a reușit."));
    }
    setExporting(false);
  };

  const dateTime = (iso: string) =>
    new Date(iso).toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="space-y-6">
      {/* Accounting export. Covers cash as well as card, because the club's
          books do not care how the money arrived. */}
      <AdminCard>
        <div className="p-4">
          <h3 className="font-bold text-slate-800">{t("onlinePayments.exportTitle", "Export contabil")}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {t(
              "onlinePayments.exportHint",
              "Un rând pentru fiecare perioadă încasată — sportiv, serviciu, brut, comision de procesare, net și numărul documentului. Include și încasările în numerar."
            )}
          </p>
          <div className="flex flex-wrap items-end gap-3 mt-4">
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-500">{t("receivables.from", "De la")}</label>
              <DateInput
                value={from}
                onChange={setFrom}
                className="px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-700"
              />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-500">{t("receivables.to", "Până la")}</label>
              <DateInput
                value={to}
                onChange={setTo}
                className="px-3 py-2 text-sm bg-white border rounded-lg border-slate-300 text-slate-700"
              />
            </div>
            <button
              onClick={runExport}
              disabled={exporting || !from || !to}
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {exporting ? t("common.loading", "Se încarcă...") : t("onlinePayments.download", "Descarcă CSV")}
            </button>
          </div>
          {exportError && <p className="mt-2 text-sm text-rose-600">{exportError}</p>}
        </div>
      </AdminCard>

      {done && (
        <div className="flex items-start gap-3 p-4 border rounded-2xl border-emerald-200 bg-emerald-50">
          <p className="flex-1 text-sm text-emerald-800">{done}</p>
          <button onClick={() => setDone(null)} className="text-sm font-semibold text-emerald-700">
            {t("common.close", "Închide")}
          </button>
        </div>
      )}

      <AdminCard>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 rounded-full border-indigo-600 animate-spin border-t-transparent" />
          </div>
        ) : error ? (
          <div className="py-16 text-center">
            <p className="text-slate-500">{t("error.loadFailed", "Nu s-au putut încărca datele")}</p>
            <button onClick={load} className="mt-3 text-sm font-semibold text-indigo-600">
              {t("common.retry", "Reîncearcă")}
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <p className="font-medium text-slate-700">{t("onlinePayments.emptyTitle", "Nicio plată online")}</p>
            <p className="mt-1 text-sm text-slate-500">
              {t(
                "onlinePayments.emptyHint",
                "Aici apar plățile cu cardul făcute de părinți prin aplicație, după ce activezi încasările."
              )}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("onlinePayments.when", "Data")}</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("onlinePayments.payer", "Plătitor")}</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">{t("onlinePayments.gross", "Brut")}</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">{t("onlinePayments.fee", "Comision")}</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">{t("onlinePayments.net", "Net")}</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("onlinePayments.document", "Document")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.ledger_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                    <td className="px-4 py-3 tabular-nums text-slate-600">{dateTime(row.occurred_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{row.payer_name || "—"}</div>
                      {row.athlete_name && <div className="text-[11px] text-slate-400">{row.athlete_name}</div>}
                      {row.initiated_by === "auto" && (
                        <div className="text-[11px] text-slate-400">{t("onlinePayments.auto", "plată automată")}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-right tabular-nums text-slate-800">
                      {formatMoney(row.gross_cents, row.currency)}
                      {row.refunded_cents > 0 && (
                        <div className="text-[11px] font-medium text-rose-600">
                          −{formatMoney(row.refunded_cents, row.currency)}{" "}
                          {t("onlinePayments.refundedTag", "restituit")}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                      {formatMoney(row.provider_fee_cents, row.currency)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700">
                      {formatMoney(row.net_cents, row.currency)}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {row.receipt_number ? (
                        <span className="tabular-nums">{row.receipt_number}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.refundable_cents > 0 ? (
                        <button
                          onClick={() => openRefund(row)}
                          className="px-3 py-1.5 text-xs font-semibold border rounded-lg border-slate-300 text-slate-700 hover:bg-slate-50"
                        >
                          {t("onlinePayments.refund", "Restituie")}
                        </button>
                      ) : (
                        <span className="text-[11px] text-slate-400">
                          {t("onlinePayments.fullyRefunded", "restituit integral")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      {refunding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-lg p-6 bg-white shadow-xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-slate-800">{t("onlinePayments.refundTitle", "Restituie bani")}</h3>
            <p className="mt-1 text-sm text-slate-500">
              {t(
                "onlinePayments.refundHint",
                "Banii se întorc din contul clubului, nu din contul platformei. Comisionul de procesare reținut de Stripe nu se mai întoarce."
              )}
            </p>

            <div className="p-3 mt-4 text-sm rounded-xl bg-slate-50">
              <div className="flex justify-between">
                <span className="text-slate-500">{t("onlinePayments.payer", "Plătitor")}</span>
                <span className="font-medium text-slate-800">{refunding.payer_name || "—"}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-500">{t("onlinePayments.refundable", "Se poate restitui")}</span>
                <span className="font-semibold tabular-nums text-slate-800">
                  {formatMoney(refunding.refundable_cents, refunding.currency)}
                </span>
              </div>
            </div>

            <label className="block mt-4 mb-1 text-xs font-semibold text-slate-500">
              {t("onlinePayments.reason", "Motiv")} *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={t("onlinePayments.reasonPlaceholder", "ex. plată dublă, copilul s-a retras")}
              className="w-full px-3 py-2 text-base border rounded-lg border-slate-300 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              {t("onlinePayments.reasonWhy", "Motivul ajunge la plătitor în notificare și rămâne în istoricul plății.")}
            </p>

            <label className="block mt-4 mb-1 text-xs font-semibold text-slate-500">
              {t("onlinePayments.partial", "Sumă parțială (RON)")}
            </label>
            <input
              value={partial}
              onChange={(e) => setPartial(e.target.value)}
              inputMode="decimal"
              placeholder={t("onlinePayments.partialPlaceholder", "lasă gol pentru restituire integrală")}
              className="w-full px-3 py-2 text-base border rounded-lg border-slate-300 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />

            {refundError && <p className="mt-3 text-sm text-rose-600">{refundError}</p>}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setRefunding(null)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 disabled:opacity-50"
              >
                {t("common.cancel", "Anulează")}
              </button>
              <button
                onClick={submitRefund}
                disabled={submitting || reason.trim().length < MIN_REASON}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50"
              >
                {submitting
                  ? t("common.loading", "Se încarcă...")
                  : t("onlinePayments.confirmRefund", "Confirmă restituirea")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
